/**
 * CommandValidator - Bounds & Safety Checks
 *
 * Validates AI-generated commands before execution.
 * Checks bounds, min sizes, font ranges, contrast, and overlap.
 * Auto-fixes invalid values when possible.
 */

import { MMOCommand, MMOEditCommand, CommandColor } from '../commands/CommandSchema';
import { RichScreenContext, RichElement } from './ScreenAnalyzer';

const CANVAS_WIDTH = 1024;
const CANVAS_HEIGHT = 748;
const MIN_VISIBLE_PERCENT = 0.2;
const MIN_SIZE = 10;
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 200;
const MIN_CONTRAST_RATIO = 3;

export interface ValidationWarning {
  type: 'contrast' | 'overlap' | 'off_screen' | 'too_small';
  message: string;
  elementIndex?: number;
}

export interface ValidationError {
  type: 'invalid_target' | 'invalid_value' | 'out_of_bounds';
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  warnings: ValidationWarning[];
  errors: ValidationError[];
  fixedCommands: MMOCommand[];
}

/**
 * Compute WCAG contrast ratio between two colors
 */
function getContrastRatio(fg: CommandColor, bg: CommandColor): number {
  const lum = (c: CommandColor) =>
    (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
  const l1 = lum(fg);
  const l2 = lum(bg);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Clamp a frame so at least MIN_VISIBLE_PERCENT remains on canvas
 */
function clampToCanvas(
  x: number,
  y: number,
  width: number,
  height: number
): { x: number; y: number; width: number; height: number } {
  const minVisibleW = width * MIN_VISIBLE_PERCENT;
  const minVisibleH = height * MIN_VISIBLE_PERCENT;

  // Ensure at least 20% of area remains visible
  const clampedX = Math.max(-width + minVisibleW, Math.min(CANVAS_WIDTH - minVisibleW, x));
  const clampedY = Math.max(-height + minVisibleH, Math.min(CANVAS_HEIGHT - minVisibleH, y));

  return { x: clampedX, y: clampedY, width, height };
}

/**
 * Ensure minimum dimensions
 */
function ensureMinSize(width: number, height: number): { width: number; height: number } {
  return {
    width: Math.max(MIN_SIZE, width),
    height: Math.max(MIN_SIZE, height),
  };
}

/**
 * Validate and fix commands before execution
 */
export function validateCommands(
  commands: MMOCommand[],
  context: RichScreenContext
): ValidationResult {
  const warnings: ValidationWarning[] = [];
  const errors: ValidationError[] = [];
  const fixedCommands: MMOCommand[] = [];

  for (const cmd of commands) {
    const fixed = validateSingleCommand(cmd, context, warnings, errors);
    if (fixed) {
      fixedCommands.push(fixed);
    }
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
    fixedCommands: errors.length === 0 ? fixedCommands : commands,
  };
}

function validateSingleCommand(
  cmd: MMOCommand,
  context: RichScreenContext,
  warnings: ValidationWarning[],
  errors: ValidationError[]
): MMOCommand | null {
  if (cmd.type === 'mmo_edit') {
    return validateMMOEdit(cmd as MMOEditCommand, context, warnings, errors);
  }

  // Non-edit commands pass through
  return { ...cmd };
}

function validateMMOEdit(
  cmd: MMOEditCommand,
  context: RichScreenContext,
  warnings: ValidationWarning[],
  errors: ValidationError[]
): MMOEditCommand {
  const mmoIndex = cmd.target.mmoIndex;
  const element = mmoIndex !== undefined ? context.elements[mmoIndex] : undefined;

  const fixedOps = cmd.operations.map((op) => {
    const fixed = { ...op };

    switch (op.property) {
      case 'font.fontSize':
      case 'fontSize': {
        let newSize = op.value;
        if (op.relative && element?.fontSize) {
          newSize = element.fontSize + op.value;
        }
        if (newSize < MIN_FONT_SIZE) {
          fixed.value = op.relative ? MIN_FONT_SIZE - (element?.fontSize || 18) : MIN_FONT_SIZE;
          warnings.push({
            type: 'too_small',
            message: `Font size clamped to ${MIN_FONT_SIZE}pt minimum`,
            elementIndex: mmoIndex,
          });
        } else if (newSize > MAX_FONT_SIZE) {
          fixed.value = op.relative ? MAX_FONT_SIZE - (element?.fontSize || 18) : MAX_FONT_SIZE;
          warnings.push({
            type: 'too_small',
            message: `Font size clamped to ${MAX_FONT_SIZE}pt maximum`,
            elementIndex: mmoIndex,
          });
        }
        break;
      }

      case 'frame.x':
      case 'x': {
        if (element) {
          let newX = op.relative ? element.frame.x + op.value : op.value;
          const clamped = clampToCanvas(newX, element.frame.y, element.frame.width, element.frame.height);
          if (clamped.x !== newX) {
            fixed.value = op.relative ? clamped.x - element.frame.x : clamped.x;
            warnings.push({
              type: 'off_screen',
              message: `Position adjusted to keep element visible`,
              elementIndex: mmoIndex,
            });
          }
        }
        break;
      }

      case 'frame.y':
      case 'y': {
        if (element) {
          let newY = op.relative ? element.frame.y + op.value : op.value;
          const clamped = clampToCanvas(element.frame.x, newY, element.frame.width, element.frame.height);
          if (clamped.y !== newY) {
            fixed.value = op.relative ? clamped.y - element.frame.y : clamped.y;
            warnings.push({
              type: 'off_screen',
              message: `Position adjusted to keep element visible`,
              elementIndex: mmoIndex,
            });
          }
        }
        break;
      }

      case 'frame.width':
      case 'width': {
        let newWidth = op.relative && element ? element.frame.width + op.value : op.value;
        const sized = ensureMinSize(newWidth, 0);
        if (sized.width !== newWidth) {
          fixed.value = op.relative && element ? sized.width - element.frame.width : sized.width;
          warnings.push({
            type: 'too_small',
            message: `Width enforced minimum ${MIN_SIZE}px`,
            elementIndex: mmoIndex,
          });
        }
        break;
      }

      case 'frame.height':
      case 'height': {
        let newHeight = op.relative && element ? element.frame.height + op.value : op.value;
        const sized = ensureMinSize(0, newHeight);
        if (sized.height !== newHeight) {
          fixed.value = op.relative && element ? sized.height - element.frame.height : sized.height;
          warnings.push({
            type: 'too_small',
            message: `Height enforced minimum ${MIN_SIZE}px`,
            elementIndex: mmoIndex,
          });
        }
        break;
      }

      case 'textColor': {
        if (op.value && typeof op.value === 'object' && 'r' in op.value) {
          const ratio = getContrastRatio(op.value as CommandColor, context.background);
          if (ratio < MIN_CONTRAST_RATIO) {
            warnings.push({
              type: 'contrast',
              message: `Text contrast ratio ${ratio.toFixed(1)}:1 is below ${MIN_CONTRAST_RATIO}:1 (may be hard to read)`,
              elementIndex: mmoIndex,
            });
          }
        }
        break;
      }
    }

    return fixed;
  });

  return {
    ...cmd,
    operations: fixedOps,
  };
}
